import { useMemo, useState } from 'react';
import { EmptyState, ErrorState, Input } from '@fiado/react-kit';
import type { LabelResolver } from '../../hooks/useLabelResolver';
import type { PermissionCatalogItem } from '../../api/types/permissions';
import type { TenantRoleListItemLocal } from '../../api/types/TenantLocal';
import { ContentLoading } from '../shared/ContentLoading';
import { UNNAMED_ROLE } from '../../lib/labelConstants';
import { buildPermissionMatrix, filterPermissionMatrix } from './permissionMatrix';

function Mark({ granted }: { granted: boolean }) {
    return granted ? (
        <span role="img" aria-label="Sí" className="font-bold text-brand-accent">
            ✓
        </span>
    ) : (
        <span role="img" aria-label="No" className="text-muted-foreground/40">
            ·
        </span>
    );
}

export interface PermissionMatrixViewProps {
    roles: TenantRoleListItemLocal[];
    catalog: PermissionCatalogItem[];
    labels: LabelResolver;
    isPending: boolean;
    isError: boolean;
    onRetry: () => void;
}

/** Matriz de solo lectura: roles contra las llaves del catálogo. Los datos los trae el contenedor. */
export function PermissionMatrixView({
    roles,
    catalog,
    labels,
    isPending,
    isError,
    onRetry,
}: PermissionMatrixViewProps) {
    const [query, setQuery] = useState('');

    const matrix = useMemo(
        () => buildPermissionMatrix(roles, catalog, labels),
        [roles, catalog, labels],
    );
    const groups = useMemo(() => filterPermissionMatrix(matrix.groups, query), [matrix, query]);

    if (isError) {
        return (
            <ErrorState
                title="No pudimos cargar la matriz de permisos"
                message="Revisa tu conexión e intenta de nuevo."
                retryLabel="Reintentar"
                onRetry={onRetry}
            />
        );
    }
    if (isPending) return <ContentLoading />;
    if (roles.length === 0) {
        return (
            <EmptyState
                title="Esta organización todavía no tiene roles"
                description="Cuando crees un rol, aquí verás sus permisos."
            />
        );
    }
    if (matrix.groups.length === 0) {
        return (
            <EmptyState
                title="No hay permisos en el catálogo"
                description="La aplicación de esta organización todavía no tiene permisos registrados."
            />
        );
    }

    const columnCount = roles.length + 1;
    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
                <Input
                    type="search"
                    aria-label="Buscar permiso"
                    placeholder="Busca por nombre o descripción"
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value);
                    }}
                    className="max-w-xs"
                />
                <p className="text-xs text-muted-foreground">
                    Solo lectura. ✓ indica que el rol tiene el permiso.
                </p>
            </div>
            <div className="overflow-x-auto rounded-md border">
                <table className="w-full border-collapse text-sm tabular-nums">
                    <thead className="bg-muted/50">
                        <tr>
                            <th scope="col" className="px-3 py-2 text-left font-semibold">
                                Permiso
                            </th>
                            {roles.map((r) => (
                                <th
                                    key={r.roleId}
                                    scope="col"
                                    className="px-3 py-2 text-center font-semibold whitespace-nowrap"
                                >
                                    {r.roleName.trim() || UNNAMED_ROLE}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {groups.length === 0 && (
                            <tr>
                                <td
                                    colSpan={columnCount}
                                    className="px-3 py-6 text-center text-muted-foreground"
                                >
                                    Ningún permiso coincide con tu búsqueda.
                                </td>
                            </tr>
                        )}
                        {groups.map((g) => [
                            <tr key={`group-${g.title}`} className="bg-muted/30">
                                <th
                                    scope="rowgroup"
                                    colSpan={columnCount}
                                    className="px-3 py-1.5 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                                >
                                    {g.title}
                                </th>
                            </tr>,
                            ...g.rows.map((row) => (
                                <tr key={row.key} className="border-t">
                                    <th scope="row" className="px-3 py-2 text-left font-normal">
                                        <span className="block font-medium">{row.name}</span>
                                        {row.description && (
                                            <span className="block text-xs text-muted-foreground">
                                                {row.description}
                                            </span>
                                        )}
                                    </th>
                                    {row.granted.map((granted, i) => (
                                        <td key={roles[i].roleId} className="px-3 py-2 text-center">
                                            <Mark granted={granted} />
                                        </td>
                                    ))}
                                </tr>
                            )),
                        ])}
                    </tbody>
                    <tfoot className="border-t bg-muted/50 text-xs text-muted-foreground">
                        <tr>
                            <th scope="row" className="px-3 py-2 text-left font-medium">
                                Permisos por rol
                            </th>
                            {matrix.totals.map((total, i) => (
                                <td key={roles[i].roleId} className="px-3 py-2 text-center">
                                    {total}
                                </td>
                            ))}
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    );
}
